import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

export const AccommodationIndividualSelectRoomOptionTileDetailsContainer = styled(VerticalSpacer)``

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  cancellationPolicy?: ReactNode
  variant: 'stacked' | 'carousel'
} & ({
  roomPackage?: App.HotelPackage
  selectedLoyaltyTargetUpgrade?: App.Package
  promotion?: undefined
  rateName?: undefined
} | {
  roomPackage?: undefined
  selectedLoyaltyTargetUpgrade?: undefined
  promotion?: App.BedbankPromotion
  rateName?: undefined
} | {
  roomPackage?: undefined
  selectedLoyaltyTargetUpgrade?: undefined
  promotion?: undefined
  rateName?: string
})

function AccommodationIndividualSelectRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    rateName,
    tagLineLabel,
    roomPackage,
    promotion,
    stayPayRateTier,
    selectedLoyaltyTargetUpgrade,
    cancellationPolicy,
    variant,
  } = props

  const intl = useIntl()

  const name = roomPackage?.name ?? promotion?.name ?? rateName
  const targetUpgradeName = selectedLoyaltyTargetUpgrade?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const packageOptionLabel = packageNumber && variant === 'carousel'
    ? intl.formatMessage(
        {
          id: '7Mi5NsVG_iGEq3sXKnR1',
          defaultMessage: 'Package option {number}',
          description: 'Label showing the package option number in the room option carousel.',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2FjAmCuPs3J95IPfxrZx',
          defaultMessage: '{tierName} member upgrade',
          description: 'Label indicating that this option includes a loyalty member upgrade.',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '-GJNF9Ml3Ajp89hqJ6kj',
          defaultMessage: 'Upgraded to {upgradeName}',
          description: 'Label indicating the selected loyalty upgrade package name.',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const nameLabel = name && stayPayRateTier
    ? intl.formatMessage(
        {
          id: 'i48WznOmxU19y14-IWRy',
          defaultMessage: '{name}, {count, plural, one {# night free} other {# nights free}}',
          description: 'Package name with included free nights suffix in individual room option tile.',
        },
        { name, count: stayPayRateTier.freeNights },
      )
    : name

  const optionWithUpgradeContent = packageOptionLabel && memberUpgradeLabel && <FormattedMessage
    id="X9NTnTF_ZIvsC-akeZkJ"
    defaultMessage="{optionLabel} | <bold>{upgradeLabel}</bold>"
    description="Package option number and member upgrade label separated by pipe in room option tile"
    values={{
      optionLabel: packageOptionLabel,
      upgradeLabel: memberUpgradeLabel,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
    }}
  />

  return <AccommodationIndividualSelectRoomOptionTileDetailsContainer gap={12}>
    <VerticalSpacer gap={4}>
      {(!!packageOptionLabel || !!memberUpgradeLabel) && <Caption variant="large">
        {optionWithUpgradeContent || packageOptionLabel || <b>{memberUpgradeLabel}</b>}
      </Caption>}
      {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
      {!!targetUpgradeName && <BodyText variant="large" weight="bold">
        {upgradedToLabel}
      </BodyText>}
      {!!name && <BodyText
        variant="large"
        weight="bold"
        colour={selectedLoyaltyTargetUpgrade ? 'neutral-four' : 'normal-contrast'}
        strikeThrough={!!selectedLoyaltyTargetUpgrade}
      >
        {nameLabel}
      </BodyText>}
      {cancellationPolicy}
    </VerticalSpacer>
    {children}
  </AccommodationIndividualSelectRoomOptionTileDetailsContainer>
}

export default AccommodationIndividualSelectRoomOptionTileDetails
